import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AchievementInfoCell,
  AlbumCell,
  AlbumCellProps,
  AlbumNode,
  AlbumNodeProps,
  SvgIcon,
  TextButton,
  TextButtonSize,
  TextButtonVariant,
} from '../../../Molecules';
import { SVGIconType } from '../../../../constants/svgIcon';
import { Profile } from '../../../../models/UserStore';
import './SmartAlbumListTemplate.css';

export type SmartAlbumListTemplateProps = {
  userProfile: Profile;
  managedClassroomExpiredAt: Date;
  level: number;
  progress: number;
  currentExp: number;
  totalExp: number;
  socialExp: number;
  knowledgeExp: number;
  recentAlbumData: AlbumNodeProps[];
  albumData: AlbumCellProps[];
  onClickInfo: () => void;
  onClickCreate: () => void;
};

export const SmartAlbumListTemplate = memo(
  ({
    userProfile,
    managedClassroomExpiredAt,
    level,
    progress,
    currentExp,
    totalExp,
    socialExp,
    knowledgeExp,
    recentAlbumData,
    albumData,
    onClickInfo,
    onClickCreate,
  }: SmartAlbumListTemplateProps) => {
    const { t, i18n } = useTranslation();
    const options: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    };
    const formattedDate = new Intl.DateTimeFormat(
      i18n.language,
      options,
    ).format(managedClassroomExpiredAt);
    return (
      <div className="smart-album-list">
        <div className="centered-container">
          <div className="title-container">
            <SvgIcon name={SVGIconType.BrainX9} className={'title-icon'} />
            <div className="title-text-container">
              <div className="title-text">{t('AI Memory Games')}</div>
              <div className="description-container">
                <div className="description-text">
                  {t('Smart Parents Program')}
                </div>
                <SvgIcon
                  name={SVGIconType.GameInstruction}
                  className={'info-icon'}
                  onClick={onClickInfo}
                />
              </div>
            </div>
          </div>

          <div className="white-banner">
            <div className="warning-text">
              {t('Valid Until: ')}
              {formattedDate}
            </div>
          </div>

          <AchievementInfoCell
            userProfile={userProfile}
            level={level}
            progress={progress}
            currentExp={currentExp}
            totalExp={totalExp}
            socialExp={socialExp}
            knowledgeExp={knowledgeExp}
          />

          {/* Temp button */}
          <div className="create-memory-games">
            <TextButton
              text={t('Create AI Memory Games')}
              size={TextButtonSize.Large}
              variant={TextButtonVariant.Cyan}
              onClick={onClickCreate}
            />
          </div>

          {recentAlbumData.length > 0 ? (
            <div className="recent-cell-container">
              <div className="title-all-component">
                <div className="recent-text">{t('Recently Played')}</div>
                {/* <div className="all-component">
                  <div className="all-text">{t('All')}</div>
                  <SvgIcon name={SVGIconType.Arrow} className="arrow-icon" />
                </div> */}
              </div>
              <div className="horizontal-list-view">
                {recentAlbumData.map((item, index) => (
                  <AlbumNode key={index} {...item} />
                ))}
              </div>
            </div>
          ) : null}

          <div className="list-view">
            {albumData.length === 0 ? (
              <div className={'empty-container'}>
                <SvgIcon
                  name={SVGIconType.BrainX9}
                  className={'empty-album-icon'}
                />
                <div className="empty-album-text">{t('No Journeys Yet')}</div>
              </div>
            ) : (
              albumData.map((item, index) => (
                <AlbumCell key={index} {...item} />
              ))
            )}
          </div>
        </div>
      </div>
    );
  },
);
